/**
 * REQUIRE-SETS — the unified "attractor" model shared by the insect and fish spawn scorers.
 *
 * A creature can be tied to the garden's composition through one or more RequireSets. Each set is BOTH a
 * potential GATE (must be satisfied ≥1 time before the creature can appear) and/or a soft DRIVER (each ADDITIONAL
 * satisfied "copy" of the set nudges the spawn/bite odds up). The `use` flag says which roles a set plays.
 *
 * Three set shapes cover everything we author:
 *   • mode "any"     — ANY-OF: "≥ `min` matching things across `items`" (interchangeable). A completed copy = a
 *                      block of `min` matches. e.g. min 1 over [clover,borage,sage] ⇒ "any one of these".
 *   • mode "all"     — COMBO / one-of-each: every item needs its own `need` count. A completed copy = one full
 *                      recipe; the number of copies = min over items of floor(count / need).
 *                      e.g. [aster×1, zinnia×1] ⇒ each aster+zinnia PAIR is one copy (driver scales per pair).
 *   • mode "cluster" — a tight CLUMP: ≥ `count` of a species (or any of `anyOf`) within `radius` garden-units.
 *                      A completed copy = one disjoint qualifying clump; copies = how many clumps exist.
 *
 * `target` picks the count source: "plant" (species counts / positions) or "decor" (placed-decor counts).
 * Decor has no positions here, so decor sets use "any"/"all" only (cluster is plant-only).
 *
 * The evaluator returns, per set, whether the GATE passes (copies ≥ 1) and how many `copies` exist — the driver
 * reads `copies` (or `copies − 1` for "extra" past the first, depending on caller). Keeping this in ONE place
 * means the insect + fish modals, scorers, and clue hints all agree on the semantics.
 */

/** One line-item inside a set: a plant-species id or decor id, with how many are needed per copy. */
export interface ReqItem { id: string; need: number; }

export type ReqMode = "any" | "all" | "cluster";
export type ReqTarget = "plant" | "decor";
/** Which roles this set plays: gate (must pass), driver (extra copies boost odds), or both. */
export type ReqUse = "gate" | "driver" | "both";

export interface RequireSet {
  /** author-facing label shown in the modal + clue hints (e.g. "nectar flowers", "a rose bed"). Optional. */
  label?: string;
  mode: ReqMode;
  target: ReqTarget;
  use: ReqUse;
  /** items for mode "any" | "all". For "any", each item's `need` is how many of THAT item count toward `min`
   *  (usually 1); the set is satisfied per `min` total matches. For "all", every item's `need` must be met. */
  items?: ReqItem[];
  /** mode "any": completed copy = this many matches across `items` (default 1). */
  min?: number;
  /** mode "cluster": the species that must clump (or use `anyOf`). */
  species?: string;
  anyOf?: string[];
  /** mode "cluster": how many within `radius` garden-units form one clump. */
  count?: number;
  radius?: number;
  /** soft-driver strength: each EXTRA satisfied copy past the first multiplies odds by (1 + perExtra), capped. */
  perExtra?: number;
}

/** The garden facts a RequireSet is evaluated against (subset of the fuller Appear/Fish ctx). */
export interface ReqCtx {
  speciesCounts: Record<string, number>;
  decorCounts: Record<string, number>;
  plantPos: Array<{ species: string; gx: number; gy: number }>;
}

export interface ReqEval {
  /** how many complete copies of the set exist right now (0 ⇒ gate fails). */
  copies: number;
  /** does the GATE pass (copies ≥ 1)? Only meaningful when the set's `use` includes "gate". */
  pass: boolean;
  /** a short human reason string for the scorer's `reasons` log. */
  reason: string;
}

/** Plain-English one-liner describing a set — shared by the modal editor card AND the clue-letter hints, so the
 *  in-game hint text always matches the authored rule. `name` maps an id to a human label (lower-cased for hints). */
export function describeSet(set: RequireSet, name: (id: string) => string = (x) => x): string {
  if (set.mode === "cluster") {
    const ids = set.anyOf?.length ? set.anyOf : set.species ? [set.species] : [];
    const names = ids.length ? ids.map(name).join(" or ") : "certain plants";
    return `${set.count ?? 3} ${names} clustered together`;
  }
  const items = set.items ?? [];
  if (!items.length) return "certain plants";
  if (set.mode === "all") {
    const parts = items.map((it) => `${it.need > 1 ? it.need + " " : ""}${name(it.id)}`);
    return `${parts.join(" and ")} together`;
  }
  const min = set.min ?? 1;
  const names = items.map((it) => name(it.id));
  return `${min > 1 ? `${min} of ` : ""}${joinOrList(names)}`;
}
function joinOrList(xs: string[]): string {
  return xs.length <= 1 ? (xs[0] ?? "") : xs.slice(0, -1).join(", ") + " or " + xs[xs.length - 1];
}

const cnt = (ctx: ReqCtx, target: ReqTarget, id: string): number =>
  (target === "decor" ? ctx.decorCounts[id] : ctx.speciesCounts[id]) ?? 0;

/** Human name hook — callers can pass a namer so reasons read nicely; defaults to the raw id. */
export function evalRequireSet(set: RequireSet, ctx: ReqCtx, name: (id: string) => string = (x) => x): ReqEval {
  if (set.mode === "cluster") {
    const ids = set.anyOf?.length ? set.anyOf : set.species ? [set.species] : [];
    const need = Math.max(1, set.count ?? 3);
    const radius = Math.max(0.5, set.radius ?? 2.5);
    const copies = countClusters(ctx.plantPos, ids, need, radius);
    const label = set.label ?? `${need}× ${ids.map(name).join("/")} within ${radius}`;
    return { copies, pass: copies >= 1, reason: `${label} ×${copies}` };
  }
  const items = set.items ?? [];
  if (!items.length) return { copies: 0, pass: false, reason: "empty set" };
  if (set.mode === "all") {
    // one-of-each recipe: copies = min over items of floor(have / need)
    let copies = Infinity;
    for (const it of items) copies = Math.min(copies, Math.floor(cnt(ctx, set.target, it.id) / Math.max(1, it.need)));
    if (!isFinite(copies)) copies = 0;
    const label = set.label ?? items.map((it) => (it.need > 1 ? `${it.need}× ` : "") + name(it.id)).join(" + ");
    return { copies, pass: copies >= 1, reason: `${label} ×${copies}` };
  }
  // mode "any": total matches across items ÷ block size (min)
  const block = Math.max(1, set.min ?? 1);
  let total = 0;
  for (const it of items) total += Math.floor(cnt(ctx, set.target, it.id));
  const copies = Math.floor(total / block);
  const label = set.label ?? `any ${block > 1 ? block + " of " : ""}[${items.map((it) => name(it.id)).join(", ")}]`;
  return { copies, pass: copies >= 1, reason: `${label} ×${copies}` };
}

/** Number of DISJOINT clumps of ≥`need` of the listed species within `radius` of a shared center. Greedy: repeatedly
 *  take the densest remaining seed's neighborhood as one clump, remove those points, repeat. Approximation (cheap,
 *  reads correctly for "how many separate clumps"). */
export function countClusters(
  plants: Array<{ species: string; gx: number; gy: number }>, ids: string[], need: number, radius: number,
): number {
  if (!ids.length || need < 1) return 0;
  const set = new Set(ids);
  let pts = plants.filter((p) => set.has(p.species)).map((p) => ({ gx: p.gx, gy: p.gy }));
  if (pts.length < need) return 0;
  const r2 = radius * radius;
  let clumps = 0;
  // up to a sane bound to avoid pathological loops
  for (let guard = 0; guard < 200 && pts.length >= need; guard++) {
    // find the point with the most neighbors (incl itself) within radius
    let bestI = -1, bestNbrs: number[] = [];
    for (let i = 0; i < pts.length; i++) {
      const nbrs: number[] = [i];
      for (let j = 0; j < pts.length; j++) {
        if (i === j) continue;
        const dx = pts[i]!.gx - pts[j]!.gx, dy = pts[i]!.gy - pts[j]!.gy;
        if (dx * dx + dy * dy <= r2) nbrs.push(j);
      }
      if (nbrs.length > bestNbrs.length) { bestNbrs = nbrs; bestI = i; }
    }
    if (bestI < 0 || bestNbrs.length < need) break;
    clumps++;
    // consume `need` points from this clump (the seed + nearest need-1), remove them
    const remove = new Set(bestNbrs.slice(0, need));
    pts = pts.filter((_, idx) => !remove.has(idx));
  }
  return clumps;
}

/** Overall summary for a roster of sets on one creature: gates (use gate/both) must ALL pass; the driver copies
 *  are the sets flagged driver/both. Returns eligibility + the combined driver multiplier + reasons. */
export function scoreRequireSets(
  sets: RequireSet[] | undefined, ctx: ReqCtx, capExtra: number, name?: (id: string) => string,
): { eligible: boolean; driver: number; reasons: string[] } {
  const reasons: string[] = [];
  if (!sets || !sets.length) return { eligible: true, driver: 1, reasons };
  let eligible = true, driver = 1;
  for (const set of sets) {
    const ev = evalRequireSet(set, ctx, name);
    const isGate = set.use === "gate" || set.use === "both";
    const isDriver = set.use === "driver" || set.use === "both";
    if (isGate && !ev.pass) { eligible = false; reasons.push(`${ev.reason} ✗ (gate)`); continue; }
    if (isGate) reasons.push(`${ev.reason} ✓`);
    if (isDriver && set.perExtra) {
      const extra = Math.min(Math.max(0, ev.copies - 1), capExtra);
      if (extra > 0) { const f = 1 + set.perExtra * extra; driver *= f; reasons.push(`+${extra} set${extra === 1 ? "" : "s"} → ×${f.toFixed(2)}`); }
    }
  }
  return { eligible, driver, reasons };
}
